/**
 * Orçamento de flush: evita rajadas de HTTP batch desnecessárias.
 */

import { getUnifiedPressureLevel } from '../runtime/pressureModel';
import { canRunFlush, getRuntimeMode } from '../runtime/runtimeMode';
import { recordAdaptiveDecision } from '../telemetry';

export const MIN_FLUSH_INTERVAL_MS = 1500;
export const MAX_BATCHES_PER_FLUSH_CYCLE = 4;
export const DEBOUNCE_FLUSH_MS = 600;
export const CONVERSION_FLUSH_MS = 250;

let lastFlushCompletedAt = 0;
let pendingFlushTimer: ReturnType<typeof setTimeout> | null = null;

export function getAdaptiveMinFlushIntervalMs(): number {
  const p = getUnifiedPressureLevel();
  if (p > 0.85) return 3000;
  if (p > 0.6) return 2000;
  return MIN_FLUSH_INTERVAL_MS;
}

export function getAdaptiveMaxBatchesPerCycle(): number {
  if (!canRunFlush()) return 0;
  const p = getUnifiedPressureLevel();
  const mode = getRuntimeMode();
  if (mode === 'safe_mode') return 0;
  if (mode === 'recovery' || mode === 'degraded') return 1;
  if (p > 0.85) return 1;
  if (p > 0.6) return 2;
  return MAX_BATCHES_PER_FLUSH_CYCLE;
}

export function markFlushCompleted(): void {
  lastFlushCompletedAt = Date.now();
}

export function getLastFlushCompletedAt(): number {
  return lastFlushCompletedAt;
}

export function clearPendingFlushTimer(): void {
  if (pendingFlushTimer) {
    clearTimeout(pendingFlushTimer);
    pendingFlushTimer = null;
  }
}

export function scheduleCoalescedFlush(run: () => void, urgent: boolean): void {
  const now = Date.now();
  const minInterval = getAdaptiveMinFlushIntervalMs();
  if (minInterval > MIN_FLUSH_INTERVAL_MS) {
    recordAdaptiveDecision('flush', `interval_${minInterval}`);
  }
  const minNext = urgent
    ? now + CONVERSION_FLUSH_MS
    : Math.max(now + DEBOUNCE_FLUSH_MS, lastFlushCompletedAt + minInterval);
  const delay = Math.max(0, minNext - now);

  if (pendingFlushTimer) clearTimeout(pendingFlushTimer);
  pendingFlushTimer = setTimeout(() => {
    pendingFlushTimer = null;
    run();
  }, delay);
}
